import axios from "axios";
import { API_URL, CUSTOMER_BASE } from "@/config";

export const customerDataHttp = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

export interface CustomerTradePhoto {
    photo_id: number;
    trade_id: number;
    photo_filename: string;
    photo_path: string;
    photo_original: string | null;
    is_primary: boolean;
    sort_order: number;
    created_at: string;
}

export interface CustomerVehicle {
    trade_id: number;
    customer_id: number;
    trade_vin: string | null;
    trade_year: number | null;
    trade_make: string | null;
    trade_model: string | null;
    trade_trim: string | null;
    trade_color: string | null;
    trade_miles: number | null;
    trade_price: string | null;
    trade_payoff: string | null;
    trade_type: string | null;       // TRADE_IN | FSBO
    trade_condition: string | null;  // EXCELLENT | GOOD | FAIR | POOR
    trade_description: string | null;
    trade_status: string | null;     // ACTIVE | SOLD | REMOVED
    created_at: string;
    updated_at: string;
    photos: CustomerTradePhoto[];
}

export interface CreateVehiclePayload {
    trade_vin?: string;
    trade_year: number;
    trade_make: string;
    trade_model: string;
    trade_trim?: string;
    trade_color?: string;
    trade_miles?: number;
    trade_price?: number;
    trade_payoff?: number;
    trade_type: "TRADE_IN" | "FSBO";
    trade_condition?: string;
    trade_description?: string;
}

export async function getMyVehicles(): Promise<CustomerVehicle[]> {
    const res = await customerDataHttp.get<CustomerVehicle[]>(`${CUSTOMER_BASE}/me/vehicles`);
    return res.data;
}

export async function getMyVehicleById(tradeId: number): Promise<CustomerVehicle> {
    const res = await customerDataHttp.get<CustomerVehicle>(`${CUSTOMER_BASE}/me/vehicles/${tradeId}`);
    return res.data;
}

export async function createMyVehicle(payload: CreateVehiclePayload): Promise<CustomerVehicle> {
    const res = await customerDataHttp.post<CustomerVehicle>(`${CUSTOMER_BASE}/me/vehicles`, payload);
    return res.data;
}

export async function uploadVehiclePhotos(tradeId: number, files: File[]): Promise<{ uploaded: number; photos: CustomerTradePhoto[] }> {
    const form = new FormData();
    files.forEach((f) => form.append("photos", f));

    const res = await axios.post(`${CUSTOMER_BASE}/me/vehicles/${tradeId}/photos`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
}

export async function deleteVehiclePhoto(tradeId: number, photoId: number): Promise<void> {
    await customerDataHttp.delete(`${CUSTOMER_BASE}/me/vehicles/${tradeId}/photos/${photoId}`);
}

export async function setPrimaryPhoto(tradeId: number, photoId: number): Promise<void> {
    await customerDataHttp.patch(`${CUSTOMER_BASE}/me/vehicles/${tradeId}/photos/${photoId}/primary`);
}

// ── Profile ──────────────────────────────────────────────────────────────────

export interface CustomerProfile {
    customer_id: number;
    customer_dealer_id: number | null;
    customer_email: string | null;
    customer_fname: string | null;
    customer_mname: string | null;
    customer_lname: string | null;
    customer_suffix: string | null;
    customer_phoneno: string | null;
    customer_cellphone: string | null;
    customer_dob: string | null;
    customer_dlno: string | null;
    customer_dlstate: string | null;
    customer_dlexpdate: string | null;
    customer_home_addr1: string | null;
    customer_home_addr2: string | null;
    customer_home_city: string | null;
    customer_home_state: string | null;
    customer_home_zip: string | null;
    customer_home_county: string | null;
    customer_home_live_years: string | null;
    customer_home_live_months: string | null;
    customer_employer_name: string | null;
    customer_employer_addr1: string | null;
    customer_employer_city: string | null;
    customer_employer_state: string | null;
    customer_employer_zip: string | null;
    customer_employer_phone: string | null;
    customer_employer_hiredate: string | null;
    customer_employer_years: string | null;
    customer_employer_months: string | null;
    customer_gross_income: string | null;
    customer_net_income: string | null;
    customer_income_frequency: string | null;
    customer_created_at: string | null;
    last_login_at: string | null;
    email_verified_at: string | null;
}

export async function getMyProfile(): Promise<CustomerProfile> {
    const res = await customerDataHttp.get<CustomerProfile>(`${CUSTOMER_BASE}/me`);
    return res.data;
}

export async function updateMyProfile(data: Partial<CustomerProfile>): Promise<CustomerProfile> {
    const res = await customerDataHttp.put<CustomerProfile>(`${CUSTOMER_BASE}/me`, data);
    return res.data;
}

// ── Credit App ───────────────────────────────────────────────────────────────

export interface CreditAppPayload {
    app_deal_id?: number;
    applicant_did?: number;
    applicant_fname: string;
    applicant_mname?: string;
    applicant_lname: string;
    applicant_suffixname?: string;
    applicant_dob?: string;
    applicant_ssn4?: string;
    applicant_driver_licenses_number?: string;
    applicant_driver_state_issued?: string;
    applicant_driver_licenses_expdate?: string;
    applicant_main_phone?: string;
    applicant_cell_phone?: string;
    applicant_email_address?: string;
    applicant_driver_licenses_expdate?: string;
    applicant_present_address1?: string;
    applicant_present_addr_city?: string;
    applicant_present_addr_state?: string;
    applicant_present_addr_zip?: string;
    applicant_addr_years?: string;
    applicant_addr_months?: string;
    applicant_buy_own_rent_other?: string;
    applicant_house_payment?: string;
    applicant_employer1_name?: string;
    applicant_employer1_addr?: string;
    applicant_employer1_city?: string;
    applicant_employer1_state?: string;
    applicant_employer1_zip?: string;
    applicant_employer1_phone?: string;
    applicant_employer1_dateofhire?: string;
    applicant_employer1_work_years?: string;
    applicant_employer1_work_months?: string;
    applicant_employer1_position?: string;
    applicant_employer1_salary_bringhome?: string;
    applicant_employer1_payday_freq?: string;
    applicant_employment_status?: string;
    applicant_other_income_amount?: string;
    applicant_other_income_source?: string;
    credit_app_source?: string;
}

export async function submitCreditApp(payload: CreditAppPayload): Promise<{ ok: boolean; id: number; token: string }> {
    const res = await customerDataHttp.post<{ ok: boolean; id: number; token: string }>(`${API_URL}/credit-apps`, payload);
    return res.data;
}
